import React, { useState } from 'react';
import { Modal, Form, Input, Select, InputNumber, message } from 'antd';
import { projectAPI } from '../../services/api';
import { useProjectStore } from '../../hooks/useProjectStore';
import { useT } from '../../i18n';

interface Props {
  open: boolean;
  onClose: () => void;
  onCreated?: (project: any) => void;
}

const WELL_TYPES = ['水平井', '定向井', '直井'];

export default function CreateProjectModal({ open, onClose, onCreated }: Props) {
  const [form] = Form.useForm();
  const [submitting, setSubmitting] = useState(false);
  const fetchProjects = useProjectStore((s) => s.fetchProjects);
  const t = useT();

  const handleSubmit = async () => {
    try {
      const values = await form.validateFields();
      setSubmitting(true);
      const { data } = await projectAPI.create(values);
      message.success(t('项目创建成功'));
      form.resetFields();
      await fetchProjects();
      onCreated?.(data);
      onClose();
    } catch (err: any) {
      if (err?.errorFields) return; // form validation
      const detail = err?.response?.data;
      const msg = typeof detail === 'string' ? detail : detail?.detail || JSON.stringify(detail || {}) || t('创建失败');
      message.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Modal
      open={open}
      title={t('新建项目')}
      onCancel={() => { form.resetFields(); onClose(); }}
      onOk={handleSubmit}
      okText={t('创建')}
      cancelText={t('取消')}
      confirmLoading={submitting}
      destroyOnClose
      width={540}
    >
      <Form
        form={form}
        layout="vertical"
        initialValues={{ well_type: '水平井', status: 'draft' }}
      >
        <Form.Item
          label={t('井名')}
          name="well_name"
          rules={[{ required: true, message: t('请输入井名') }]}
        >
          <Input placeholder={t('例如：FY-H1')} maxLength={100} />
        </Form.Item>

        <Form.Item
          label={t('项目名')}
          name="name"
          rules={[{ required: true, message: t('请输入项目名') }]}
        >
          <Input placeholder={t('例如：龙马溪页岩气压裂项目')} maxLength={200} />
        </Form.Item>

        <Form.Item
          label={t('油田')}
          name="field_name"
          rules={[{ required: true, message: t('请输入油田名') }]}
        >
          <Input placeholder={t('例如：涪陵气田')} maxLength={100} />
        </Form.Item>

        <Form.Item
          label={t('井型')}
          name="well_type"
          rules={[{ required: true, message: t('请选择井型') }]}
        >
          <Select options={WELL_TYPES.map((v) => ({ label: t(v), value: v }))} />
        </Form.Item>

        <Form.Item label={t('目标层位')} name="target_formation">
          <Input placeholder={t('例如：龙马溪组下段')} maxLength={100} />
        </Form.Item>

        <Form.Item label={t('总井深 (m)')} name="total_depth">
          <InputNumber min={0} max={20000} style={{ width: '100%' }} placeholder={t('例如：4280')} />
        </Form.Item>

        <Form.Item label={t('备注')} name="description">
          <Input.TextArea rows={3} maxLength={500} placeholder={t('项目描述（选填）')} />
        </Form.Item>
      </Form>
    </Modal>
  );
}
